import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Background,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { Maximize2, Minimize2 } from 'lucide-react'
import { Button, Tooltip } from 'antd'
import type { C2C1MappingPayload, FlowPartyKind, FlowPartyNode } from '@/types'
import { cn } from '@/utils/cn'

type PartyNodeData = {
  code: string
  name: string | null
  kind: FlowPartyKind
  highlighted?: boolean
}

function PartyNode({ data }: NodeProps) {
  const d = data as PartyNodeData
  const isEndUser = d.kind === 'end_user'
  const isPlantOrigin = d.kind === 'plant'
  const isPlantReturn = d.kind === 'plant_return'
  const isForward = d.kind === 'forward_customer'

  const shell = isEndUser
    ? 'border-[var(--leap-primary)] bg-[#af0000] text-white'
    : isPlantOrigin
      ? 'border-slate-700 bg-slate-800 text-white'
      : isPlantReturn
        ? 'border-emerald-700 bg-emerald-800 text-white'
        : isForward
          ? 'border-amber-600 bg-amber-50 text-[var(--foreground)]'
          : 'border-slate-300 bg-white text-[var(--foreground)]'

  const roleLabel = isEndUser
    ? 'End user · C2'
    : isPlantOrigin
      ? 'LEAP plant · origin'
      : isPlantReturn
        ? 'LEAP plant · return'
        : isForward
          ? 'Next customer · flow'
          : 'C1 / C2'

  return (
    <div
      className={`min-w-[200px] max-w-[260px] rounded-xl border-2 px-3 py-2.5 shadow-sm ${shell} ${
        d.highlighted ? 'ring-2 ring-offset-2 ring-amber-400' : ''
      }`}
    >
      <Handle type="target" position={Position.Left} className="!bg-slate-400" />
      <div
        className={`text-[10px] font-semibold uppercase tracking-wider ${
          isEndUser || isPlantOrigin || isPlantReturn
            ? 'text-white/70'
            : 'text-muted-foreground'
        }`}
      >
        {roleLabel}
      </div>
      <div className="mt-0.5 truncate font-mono text-sm font-semibold">{d.code}</div>
      {d.name ? (
        <div
          className={`mt-1 line-clamp-2 text-xs leading-snug ${
            isEndUser || isPlantOrigin || isPlantReturn ? 'text-white/85' : 'text-muted-foreground'
          }`}
        >
          {d.name}
        </div>
      ) : null}
      <Handle type="source" position={Position.Right} className="!bg-slate-400" />
    </div>
  )
}

const nodeTypes = { party: PartyNode }

const NODE_H = 96
const GAP_Y = 24
const COL_W = 320

/** Shared look for every location→location edge on the flow canvas. */
const EDGE_DASH = '6 4'

function nodeId(n: FlowPartyNode) {
  return n.id || n.code
}

function edgeColor(edgeKind: string, active: boolean): string {
  if (!active) return '#94a3b8'
  if (edgeKind === 'plant_return') return '#047857'
  if (edgeKind === 'plant') return '#334155'
  if (edgeKind === 'flow_forward') return '#d97706'
  return '#af0000'
}

function buildGraph(payload: C2C1MappingPayload): { nodes: Node[]; edges: Edge[] } {
  const flowNodes: FlowPartyNode[] =
    payload.nodes?.length > 0
      ? payload.nodes
      : [
          ...(payload.c1Customers ?? []).map((c) => ({
            id: c.code,
            code: c.code,
            name: c.name,
            kind: 'customer' as const,
            depth: 1,
          })),
          {
            id: payload.bpCode,
            code: payload.bpCode,
            name: null,
            kind: 'end_user' as const,
            depth: 0,
          },
        ]

  const maxDepth = Math.max(payload.maxDepth ?? 0, ...flowNodes.map((n) => n.depth), 0)

  const byDepth = new Map<number, FlowPartyNode[]>()
  for (const n of flowNodes) {
    const list = byDepth.get(n.depth) ?? []
    list.push(n)
    byDepth.set(n.depth, list)
  }
  for (const list of byDepth.values()) {
    list.sort((a, b) => a.code.localeCompare(b.code))
  }

  const colHeights = [...byDepth.entries()].map(
    ([, list]) => Math.max(list.length, 1) * (NODE_H + GAP_Y) - GAP_Y
  )
  const maxColHeight = Math.max(0, ...colHeights, NODE_H)

  const nodes: Node[] = flowNodes.map((n) => {
    const list = byDepth.get(n.depth) ?? [n]
    const index = list.findIndex((x) => nodeId(x) === nodeId(n))
    const colHeight = Math.max(list.length, 1) * (NODE_H + GAP_Y) - GAP_Y
    const yOffset = Math.max(0, (maxColHeight - colHeight) / 2)
    const x = (maxDepth - n.depth) * COL_W + 40
    return {
      id: nodeId(n),
      type: 'party',
      position: { x, y: yOffset + index * (NODE_H + GAP_Y) },
      data: {
        code: n.code,
        name: n.name,
        kind: n.kind,
        highlighted: n.code === payload.bpCode && n.kind === 'end_user',
      },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    }
  })

  const flowEdges =
    payload.edges?.length > 0
      ? payload.edges
      : (payload.mappings ?? []).map((m) => ({
          sourceCode: m.c1Customer,
          targetCode: m.c2Customer,
          contract: m.contract || null,
          edgeKind: (m.edgeKind ?? 'contract') as
            | 'contract'
            | 'plant'
            | 'plant_return'
            | 'flow_forward',
          scannableTagCount: m.scannableTagCount ?? 0,
        }))

  const edgeKeys = new Set<string>()
  const edges: Edge[] = []
  for (const row of flowEdges) {
    const id = `${row.edgeKind}:${row.sourceCode}->${row.targetCode}:${row.contract ?? ''}`
    if (edgeKeys.has(id)) continue
    edgeKeys.add(id)
    const tagCount = row.scannableTagCount ?? 0
    const tagLabel = `${tagCount} tag${tagCount === 1 ? '' : 's'}`
    const label =
      row.edgeKind === 'plant'
        ? `LEAPOUT · ${tagLabel}`
        : row.edgeKind === 'plant_return'
          ? `CUSTOUT · ${tagLabel}`
          : row.edgeKind === 'flow_forward'
            ? `Flow · ${tagLabel}`
            : row.contract
              ? `${row.contract} · ${tagLabel}`
              : tagLabel
    const active = tagCount > 0
    const color = edgeColor(row.edgeKind, active)
    edges.push({
      id,
      source: row.sourceCode,
      target: row.targetCode,
      label,
      animated: true,
      style: {
        stroke: color,
        strokeWidth: active ? 2 : 1.5,
        strokeDasharray: EDGE_DASH,
      },
      labelStyle: { fontSize: 10, fill: '#334155', fontWeight: 600 },
      labelBgStyle: { fill: '#ffffff', fillOpacity: 0.95 },
      labelBgPadding: [5, 8] as [number, number],
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color,
        width: 16,
        height: 16,
      },
    })
  }

  return { nodes, edges }
}

export function C2C1FlowGraph({ payload }: { payload: C2C1MappingPayload }) {
  const graph = useMemo(() => buildGraph(payload), [payload])
  const [nodes, setNodes, onNodesChange] = useNodesState(graph.nodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(graph.edges)
  const canvasRef = useRef<HTMLDivElement>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)

  useEffect(() => {
    setNodes(graph.nodes)
    setEdges(graph.edges)
  }, [graph, setNodes, setEdges])

  useEffect(() => {
    const onFsChange = () => {
      const el = canvasRef.current
      setIsFullscreen(Boolean(el && document.fullscreenElement === el))
    }
    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
  }, [])

  const toggleFullscreen = useCallback(async () => {
    const el = canvasRef.current
    if (!el) return
    try {
      if (document.fullscreenElement === el) {
        await document.exitFullscreen()
      } else if (!document.fullscreenElement) {
        await el.requestFullscreen()
      }
    } catch {
      // Browser may block fullscreen without a user gesture or support.
    }
  }, [])

  const hasEdges = (payload.edges?.length ?? 0) > 0 || (payload.mappings?.length ?? 0) > 0

  if (!hasEdges && (payload.nodes?.length ?? 0) <= 1) {
    return (
      <div className="flex h-[360px] items-center justify-center rounded-xl border border-dashed border-border bg-muted/40 text-sm text-muted-foreground">
        No upstream C1 / LEAP plant flow found for this BP.
      </div>
    )
  }

  return (
    <div
      ref={canvasRef}
      className={cn(
        'relative w-full overflow-hidden border border-border bg-[#f8fafc]',
        isFullscreen
          ? 'h-screen rounded-none border-0'
          : 'h-[min(75vh,640px)] rounded-xl'
      )}
    >
      <div className="absolute right-3 top-3 z-10">
        <Tooltip title={isFullscreen ? 'Exit full screen' : 'Full screen diagram'}>
          <Button
            type="default"
            size="small"
            icon={isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            onClick={() => void toggleFullscreen()}
            aria-label={isFullscreen ? 'Exit full screen' : 'Enter full screen'}
            className="!bg-white/95 shadow-sm"
          >
            {isFullscreen ? 'Exit' : 'Full screen'}
          </Button>
        </Tooltip>
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.18 }}
        minZoom={0.2}
        maxZoom={1.6}
        proOptions={{ hideAttribution: true }}
        className="h-full w-full"
      >
        <Background gap={18} size={1} color="#e2e8f0" />
        <Controls showInteractive={false} />
        <MiniMap
          nodeStrokeWidth={2}
          pannable
          zoomable
          className="!bg-white/90"
          nodeColor={(n) => {
            const kind = (n.data as PartyNodeData)?.kind
            if (kind === 'end_user') return '#af0000'
            if (kind === 'plant') return '#1e293b'
            if (kind === 'plant_return') return '#047857'
            if (kind === 'forward_customer') return '#d97706'
            return '#94a3b8'
          }}
        />
      </ReactFlow>
    </div>
  )
}
